import { FormattedMessage, useIntl } from '@liveauctioneers/hammer-ui-core/intl';
import { getBidderPhoneNumber } from '@/redux/modules/account/user/user.selectors';
import {
    getE164FormattedNumber,
    getNationalPhoneNumberForm,
} from '@liveauctioneers/caterwaul-components/lib/Forms/PhoneForm/utils/phoneForm';
import { useAppSelector } from '@/redux/hooks';
import { email as validateEmail } from '@/utils/validation';
import FormInput from '@/components/Input/FormInput';
import isEqual from 'react-fast-compare';
import PhoneForm, { PhoneFormShape } from '@liveauctioneers/caterwaul-components/lib/Forms/PhoneForm/PhoneForm';
import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import usePrevious from '@liveauctioneers/caterwaul-components/lib/hooks/usePrevious';

type FieldErrors = {
    email: string;
    firstName: string;
    lastName: string;
    mobileNumber: string;
};

type FieldValid = {
    email: boolean;
    firstName: boolean;
    lastName: boolean;
    mobileNumber: boolean;
};

export type ProfileBasicsData = {
    email: string;
    firstName: string;
    lastName: string;
    mobileNumber: string;
};

type ProfileBasicsMeta = {
    fieldErrors: FieldErrors;
    fieldValid: FieldValid;
};

export type ProfileBasicsFormShape = ProfileBasicsData & ProfileBasicsMeta;

interface ProfileBasicsFormProps {
    readonly className?: string;
    readonly formData: ProfileBasicsData;
    readonly mobileVerified?: boolean;
    readonly updateParentForm: (formValid: boolean, formBasics: ProfileBasicsData) => void;
}

const ProfileBasicsForm = ({ className, formData, mobileVerified, updateParentForm }: ProfileBasicsFormProps) => {
    const basicsForm = useRef<ProfileBasicsFormShape>({
        email: formData.email,
        fieldErrors: {
            email: 'formErrors.none',
            firstName: 'formErrors.none',
            lastName: 'formErrors.none',
            mobileNumber: 'formErrors.none',
        },
        fieldValid: {
            email: true,
            firstName: true,
            lastName: true,
            mobileNumber: true,
        },
        firstName: formData.firstName,
        lastName: formData.lastName,
        mobileNumber: formData.mobileNumber,
    });

    const { formatMessage } = useIntl();
    const phoneNumberForm = getNationalPhoneNumberForm(basicsForm.current.mobileNumber);
    const [shouldUpdateParentForm, setShouldUpdateParentForm] = useState<boolean>(false);
    const [updatedPhone, setUpdatedPhone] = useState<boolean>(false);
    const [phoneNumber, setPhoneNumber] = useState<PhoneFormShape>(phoneNumberForm);
    const accountPhoneNumber = useAppSelector((state) => getBidderPhoneNumber(state));
    const prevForm = usePrevious(formData);
    const isVerified = mobileVerified && accountPhoneNumber === basicsForm.current.mobileNumber;

    useEffect(() => {
        if (!isEqual(formData, prevForm)) {
            basicsForm.current = {
                ...basicsForm.current,
                email: formData.email,
                firstName: formData.firstName,
                lastName: formData.lastName,
                mobileNumber: formData.mobileNumber,
            };
        }
    }, [formData, prevForm]);

    useEffect(() => {
        if (!shouldUpdateParentForm) {
            return;
        }
        const formValid = basicsForm.current.fieldValid.email && basicsForm.current.fieldValid.mobileNumber;

        updateParentForm(formValid, {
            email: basicsForm.current.email,
            firstName: basicsForm.current.firstName,
            lastName: basicsForm.current.lastName,
            mobileNumber: basicsForm.current.mobileNumber,
        } as ProfileBasicsData);
        setShouldUpdateParentForm(false);
    }, [updateParentForm, shouldUpdateParentForm]);

    const handlePhoneFormChange = React.useCallback((phoneForm: PhoneFormShape, isValid: boolean) => {
        const pnum = getE164FormattedNumber(phoneForm).substring(1);

        if (!isValid) {
            basicsForm.current.fieldErrors.mobileNumber = 'formErrors.mobileNumberInvalid';
            basicsForm.current.fieldValid.mobileNumber = false;
        } else {
            basicsForm.current.fieldErrors.mobileNumber = 'formErrors.none';
            basicsForm.current.fieldValid.mobileNumber = true;
        }
        basicsForm.current.mobileNumber = pnum;

        setShouldUpdateParentForm(true);
        setPhoneNumber(phoneForm);
        setUpdatedPhone(true);
    }, []);

    const validateForm = React.useCallback((fieldName: string, value: string) => {
        const { fieldErrors, fieldValid } = basicsForm.current;
        let valid = true;
        let errorMessage = '';

        if (fieldName === 'email') {
            if (value === '' || !validateEmail(value)) {
                valid = false;
                errorMessage = 'formErrors.emailEmpty';
            }
        }

        const updatedFieldsValue = {
            ...fieldValid,
            [fieldName]: valid,
        };

        const updatedFieldErrors = {
            ...fieldErrors,
            [fieldName]: errorMessage,
        };

        basicsForm.current = {
            ...basicsForm.current,
            fieldErrors: updatedFieldErrors,
            [fieldName]: value,
            fieldValid: updatedFieldsValue,
        };
    }, []);

    const handleFormChange = React.useCallback(
        (event: { target: any }) => {
            setShouldUpdateParentForm(true);
            validateForm(event.target.id, event.target.value);
        },
        [validateForm]
    );

    return (
        <StyledForm className={className}>
            <StyledRow>
                <StyledFormInputHalf
                    autoComplete="given-name"
                    form={basicsForm.current}
                    id="firstName"
                    onChange={handleFormChange}
                    testid="accountSettingsFirstName"
                    value={basicsForm.current.firstName || ''}
                />
                <StyledFormInputHalf
                    autoComplete="family-name"
                    form={basicsForm.current}
                    id="lastName"
                    onChange={handleFormChange}
                    testid="accountSettingsLastName"
                    value={basicsForm.current.lastName || ''}
                />
            </StyledRow>
            <StyledRow>
                <StyledFormInputFull
                    autoComplete="email"
                    form={basicsForm.current}
                    id="email"
                    onChange={handleFormChange}
                    testid="accountSettingsEmailInput"
                    value={basicsForm.current.email || ''}
                />
            </StyledRow>
            <StyledRow>
                <StyledPhoneForm
                    errorMessage={formatMessage({
                        id: 'formErrors.mobileNumberInvalid',
                    })}
                    onChange={handlePhoneFormChange}
                    phoneLabel={<FormattedMessage id="completeAccountModal.mobilePhone" />}
                    phoneNumber={phoneNumber}
                    showCountryCode
                    submitted={updatedPhone}
                    testid="accountSettingsMobile"
                    verified={isVerified}
                />
            </StyledRow>
        </StyledForm>
    );
};

export default React.memo(ProfileBasicsForm);

const StyledForm = styled.div`
    display: block;
`;

const StyledRow = styled.div`
    flex-direction: row;
    display: flex;
    justify-content: space-between;
`;

const StyledFormInputHalf = styled(FormInput)`
    width: calc(50% - 12px);
`;

const StyledFormInputFull = styled(FormInput)`
    width: 100%;
`;

const StyledPhoneForm = styled(PhoneForm)`
    width: calc(50% - 12px);
    @media (max-width: ${({ theme }) => theme.breakpoints.tabletNarrowWidth}) {
        width: 100%;
        max-width: 100%;
    }
`;
